import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Paper,
  Box,
  CircularProgress,
  Button,
  Grid,
  Divider,
  Tabs,
  Tab,
  Chip,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { adminAPI, approvalsAPI } from '../../services/api';

// Marathi label mapping for form fields
const FIELD_LABELS = {
  // Identification fields
  district: 'जिल्हा',
  taluka: 'तालुका',
  talukaOther: 'इतर तालुका',
  village: 'गाव',
  ashaName: 'आशा कार्यकर्ता नाव',
  ashaCode: 'आशा कोड',
  anmName: 'ANM नाव',
  subCentre: 'उपकेंद्र',
  phcChc: 'प्राथमिक आरोग्य केंद्र',
  gatpravartakName: 'नियुक्त गटप्रवर्तक',

  // HBNC mother IDs, DOB, LMP, delivery time
  motherAbha: 'आईचा ABHA क्रमांक',
  motherAadhaar: 'आईचा आधार क्रमांक',
  motherDob: 'आईची जन्मतारीख',
  lmpDate: 'शेवटच्या मासिक पाळीची तारीख (LMP)',
  expectedDeliveryDate: 'प्रसूतीची अपेक्षित तारीख',
  deliveryTime: 'प्रसूतीची वेळ',
  pretermDelivery: 'अपुऱ्या दिवसांची प्रसूती',
  numberOfBabies: 'जन्मलेल्या बाळांची संख्या',
  gatpravartakSignature: 'गटप्रवर्तक नाव व स्वाक्षरी',

  // Mother & Newborn fields
  motherName: 'आईचे नाव',
  motherAge: 'आईचे वय',
  motherAddress: 'पत्ता',
  mobileNumber: 'मोबाइल नंबर',
  deliveryDate: 'प्रसूती तारीख',
  deliveryPlace: 'प्रसूती ठिकाण',
  deliveryType: 'प्रसूतीचा प्रकार',
  babyName: 'बालाचे नाव',
  childName: 'बालाचे नाव',
  gender: 'लिंग',
  birthWeight: 'जन्माचे वजन (किलो)',
  gestationalAge: 'गर्भावस्था काळ (जुनी नोंद)',
  birthOrder: 'खेप',
  dateOfBirth: 'जन्म तारीख',

  // Child Profile fields (HBYC)
  fatherName: 'वडिलांचे नाव',
  motherMobile: 'आईचा मोबाइल',
  houseNumber: 'घर क्रमांक',

  // Visit fields
  date: 'तारीख',
  visitDate: 'भेटीची तारीख',
  notes: 'टिपा',
  status: 'स्थिती',
  weight: 'वजन',
  height: 'उंची',
  temperature: 'तापमान',

  // Health Assessment (आरोग्य तपासणी — HBNC प्रत्येक भेटीत)
  healthIndicators: 'आरोग्य सूचक',
  dangerSigns: 'धोक्याची चिन्हे',
  referralNeeded: 'संदर्भ आवश्यक',
  referralPlace: 'संदर्भ ठिकाण',
  referralReason: 'संदर्भ कारण',
  isReferred: 'रेफर केले आहे का?',
  referredTo: 'कोणत्या सुविधेकडे रेफर केले',
  referralStatus: 'रेफरल स्थिती',
  motherPostnatalStatus: 'आईची प्रसुतिपश्चात स्थिती',
  motherComplications: 'गुंतागुंत तपशील',
  otherObservations: 'इतर निरीक्षणे',

  // Counseling (समुपदेशन — HBNC प्रत्येक भेटीत)
  newbornCareTopics: 'नवजात काळजी विषय',
  motherCareTopics: 'आई काळजी विषय',
  hygieneTopics: 'स्वच्छता विषय',
  hygieneSanitationTopics: 'स्वच्छता आणि सॅनिटेशन विषय',
  iecMaterialsProvided: 'IEC साहित्य प्रदान केले',
  iecMaterial: 'दिलेले IEC साहित्य',
  familyParticipation: 'कुटुंब सहभाग',
  otherTopics: 'इतर विषय / टिप्पण्या',

  // Nutrition (HBYC)
  exclusiveBreastfeeding: 'केवळ स्तनपान',
  complementaryFeeding: 'पूरक आहार',
  feedingQuantity: 'आहार प्रमाण',
  feedingFrequency: 'आहार वारंवारता',

  // Health status (HBYC)
  diarrhea: 'अतिसार',
  orsGiven: 'ORS दिले',
  ifaSyrup: 'IFA सिरप',
  immunizationStatus: 'लसीकरण स्थिती',

  // Hygiene
  handwashing: 'हात धुणे',
  cleanWater: 'स्वच्छ पाणी',

  // HBYC age-wise assessment checklist (आशाने वयानुसार तपासणी करावी)
  isSick: 'बाळ आजारी आहे का?',
  sicknessDetails: 'आजाराचा तपशील',
  referredIfSick: 'आजारी असल्यास आरोग्य केंद्रात संदर्भित केले काय?',
  breastfeedingContinuing: 'स्तनपान चालू आहे काय?',
  exclusiveBreastfeedingAdvice: 'निव्वळ स्तनपान देण्याचा सल्ला',
  complementaryFeedingAdvice: 'पूरक आहार देण्याचा सल्ला',
  complementaryFeedingGiven: 'पूरक आहार दिला जात आहे का?',
  complementaryFeedingQuantity1: 'पूरक आहार: २-३ चमचे, २-३ वेळा/दिवस',
  complementaryFeedingQuantity2: 'पूरक आहार: १/२ कप, २-३ वेळा/दिवस + न्याहारी',
  complementaryFeedingQuantity3: 'पूरक आहार: १/२ कप, ३-४ वेळा/दिवस + न्याहारी',
  complementaryFeedingQuantity4: 'पूरक आहार: ३/४-१ कप, ३-४ वेळा/दिवस + न्याहारी',
  weightAsPerAge: 'वयानुसार वजन (किलोग्रॅम)',
  heightRecordedByAnganwadi: 'अंगणवाडी सेविकेने उंचीची नोंद केली काय?',
  heightAsPerAge: 'वयानुसार उंची (सेमी)',
  severelyUnderweightRedBand: 'तीव्र कमी वजनाचे (लाल पट्टा) आहे काय?',
  referredToNRC: 'NRC ला संदर्भित केले काय?',
  nrcReferralLocation: 'कुठे केले',
  childCareAdvice: 'बालसंगोपनाचा सल्ला',
  developmentalDelayFound: 'विकासात्मक विलंब आढळला आहे काय?',
  developmentalDelayAreas: 'विलंबाचे क्षेत्र',
  referredForDevelopmentalDelay: 'विकासात्मक विलंबासाठी संदर्भित केले आहे काय?',
  immunizationUpToDate: 'वयानुसार लसीकरण केले आहे काय?',
  mrVaccineGiven: 'गोवर रुबेला लस घेतली का?',
  vitaminAGiven: 'अ जीवनसत्व दिले आहे काय?',
  orsAvailableAtHome: 'घरी ORS आहे काय?',
  orsGivenAtThisVisit: 'ORS दिले आहे का?',
  ifaSyrupAvailableAtHome: 'घरी IFA सीरप आहे काय?',
  ifaSyrupGivenAtThisVisit: 'IFA सीरप दिले आहे का?',
  ageAppropriateServiceGiven: 'आशाने वयानुसार सेवा दिली का?',
  handwashingAdvice: 'हात स्वच्छ धुण्याचा सल्ला',
  familyPlanningAdvice: 'कुटुंब नियोजनाचा सल्ला',

  // Outcome
  finalStatus: 'अंतिम स्थिती',
  finalDevelopmentStatus: 'अंतिम विकास स्थिती',
  completionDate: 'पूर्णता तारीख',
  ashaSignature: 'आशा स्वाक्षरी',
  supervisorSignature: 'पर्यवेक्षक स्वाक्षरी',
  allVisitsCompleted: 'सर्व भेटी पूर्ण',
  underTreatment: 'उपचार सुरू',
  referred: 'संदर्भित',
  death: 'मृत्यू',
  deathDate: 'मृत्यू तारीख',
  deathReason: 'मृत्यूचे कारण',

  // Common fields
  remarks: 'शेरा',
  other: 'इतर',
  yes: 'होय',
  no: 'नाही',
  address: 'पत्ता',

  // Outcome / भेट समाप्ती — shared across HBNC, HBYC, NRC, SNCU
  overallRemarks: 'एकूण निरीक्षणे आणि शिफारसी',
  supervisorName: 'पर्यवेक्षकाचे नाव',
  supervisorDate: 'पर्यवेक्षक तारीख',
  supervisorSignatureDate: 'पर्यवेक्षक स्वाक्षरी तारीख',
  ashaSignatureDate: 'आशा स्वाक्षरी तारीख',
  treatmentDetails: 'उपचाराचे तपशील',
  referredFacility: 'कोणत्या सुविधेकडे रेफर केले',
  incompleteReason: 'अपूर्ण असण्याचे कारण',
  deathCause: 'मृत्यूचे कारण',

  // HBNC motherNewborn — birth weight (legacy pair, kept alongside birthWeight)
  birthWeightKg: 'जन्माचे वजन (किलो)',
  birthWeightGms: 'जन्माचे वजन (ग्रॅम)',

  // Legacy HBNC visit fields (pre-redesign schema, kept for old records)
  cordStatus: 'नाभी स्थिती',
  feedingStatus: 'स्तनपान स्थिती',

  // Legacy HBYC visit fields (pre-redesign schema, kept for old records)
  hadDiarrhea: 'अतिसार झाला का?',
  immunization: 'लसीकरण',
  properFrequency: 'योग्य वारंवारता',
  referralRequired: 'संदर्भ आवश्यक आहे का?',
  adequateQuantity: 'पुरेसे प्रमाण',
  counselingProvided: 'दिलेले समुपदेशन विषय',

  // NRC follow-up visit
  diet: 'आहार',

  // HBNC mother & newborn home-visit assessment checklist
  babyAlive: 'बाळ जिवंत आहे का? (जुनी नोंद)',
  deathDateTime: 'मृत्युचा दिनांक व वेळ (जुनी नोंद)',
  motherMealsPerDay: 'माता २४ तासात किती वेळा जेवण करते',
  fullMealAdviceGiven: 'पूर्ण जेवण घेण्याचा सल्ला दिला का?',
  padsChangedPerDay: 'एका दिवसात बदललेल्या घड्या/पॅडची संख्या',
  referredForBleeding: 'रक्तस्रावासाठी रुग्णालयात पाठवले का?',
  infantKeptWarm: 'शिशूला उबदार ठेवले जाते का?',
  properBreastfeeding: 'शिशूला व्यवस्थित स्तनपान केले जाते का?',
  excessiveCryingOrLowUrination: 'सतत रडणे / कमी लघवी',
  motherTemperature: 'मातेचे तापमान (°F)',
  foulDischargeWithFever: 'दुर्गंधयुक्त स्त्राव व ताप',
  motherIncoherentOrConvulsions: 'असंबद्ध बोलणे / झटके',
  lowOrNoMilkSupply: 'दुध न येणे / कमी येणे',
  nippleSoreOrBreastLump: 'स्तनाग्राला जखम होणे / दुखणे / स्तनात गाठ',
  motherFrequentVomiting: 'मातेला वारंवार उलट्या होत आहेत का?',
  eyesSwollenOrPus: 'डोळे सुजलेले / पू भरलेले',
  babyWeightKg: 'शिशूचे वजन (किलो)',
  weightGained: 'बाळाच्या वजनात वाढ झाली आहे का?',
  babyTemperature: 'शिशूचे तापमान (°F)',
  jaundice: 'डोळ्यात / त्वचेवर पिवळेपणा (कावीळ)',
  bcgVaccineGiven: 'BCG लस मिळाली का?',
  polioVaccineGiven: 'पोलीओ लस मिळाली का?',
  hepatitisBVaccineGiven: 'हिपॅटायटीस बी लस मिळाली का?',
  newbornReferred: 'नवजात बालकाला संदर्भित केले का?',
  newbornReferralDate: 'नवजात संदर्भाची तारीख',
  newbornReferralReason: 'नवजात संदर्भाचे कारण',
  newbornReferralInstitution: 'नवजात संदर्भ संस्थेचे नाव',
  motherReferred: 'मातेला संदर्भित केले का?',
  motherReferralDate: 'माता संदर्भाची तारीख',
  motherReferralReason: 'माता संदर्भाचे कारण',
  motherReferralInstitution: 'माता संदर्भ संस्थेचे नाव',
  allLimbsLimp: 'सर्व हातपाय लुळे आहेत का?',
  reducedOrStoppedFeeding: 'दूध कमी/बंद पीत आहे का?',
  weakOrNoCrying: 'रडणे क्षीण/बंद आहे का?',
  abdominalDistensionOrVomiting: 'पोट फुगणे / वारंवार उलटी',
  bodyColdOrFeverReported: 'शरीर थंड पडणे / ताप',
  chestIndrawing: 'छाती आत ओढली जाणे',
  respiratoryRateHigh: 'श्वसन दर ६०+ प्रति मिनिट',
  umbilicalCordPus: 'नाळेत पु होणे',

  // मातेच्या - नवजात अर्भकाच्या भेटीसाठी गृहभेट कार्ड (HBNC motherNewborn additions)
  stillbirth: 'उपजत मृत्यु झाला आहे का?',
  breastfeedingStartTiming: 'स्तनपान सुरु केले',
  motherDischargeDate: 'रुग्णालयातून सोडल्याची तारीख (आई)',
  babyDischargeDate: 'रुग्णालयातून सोडल्याची तारीख (बाळ)',
  birthRegistrationDone: 'जन्म नोंदणी केली आहे का?',
  rchIdNumber: 'आरसीएच (RCH) आयडी क्रमांक (जुनी नोंद)',

  // ग) नवजात शिशुची पहिली तपासणी — HBNC visit_1 day-1-only fields
  babyPreterm: 'बाळ कमी दिवसाचे आहे का?',
  gestationWeeksAtDelivery: 'किती आठवड्यात मातेची प्रसुती झाली',
  firstExamTimePeriod: 'प्रथम तपासणीची वेळ',
  firstExamTime: 'तपासणीची नेमकी वेळ',
  motherAlive: 'माता जिवंत आहे काय?',
  motherDeathDateTime: 'मातेच्या मृत्युचा दिनांक व वेळ',
  motherExcessiveBleeding: 'अत्याधिक रक्तस्राव',
  motherUnconsciousOrConvulsions: 'बेशुद्ध / झटके',
  firstFeedGiven: 'शिशूला प्रथम आहाराच्या रूपात दिलेले',
  firstBreastfeedTime: 'शिशूला प्रथम स्तनपान दिलेली वेळ',
  babyFeedingManner: 'शिशू दूध कसे प्यायले',
  motherBreastfeedingDifficulty: 'मातेला स्तनपान देण्यास अडचण आली का?',
  motherBreastfeedingDifficultyDetails: 'स्तनपान अडचणीचा तपशील',
  headPusOrSwelling: 'डोक्यातून पू वाहणे किंवा सूज',
  cordBleeding: 'नाभीतून रक्त निघत आहे का?',
  weighingScaleColor: 'वजन काट्यावरचा रंग',
  newbornDried: 'नवजात बालकास कोरडे केले का?',
  newbornWarmthCareGiven: 'नवजात बालकास उबदार ठेवणे, अंघोळ न घालणे, कपड्यात मुडाळणे',
  newbornExclusiveBreastfeeding: 'नवजात बालकास निव्वळ आईचे दूध पाजले का?',
  babyDeformityFound: 'शिशूमध्ये काही व्यंग दिसत आहे का?',
  babyDeformityTypes: 'व्यंगाचा प्रकार',
  babyDeformityReferred: 'व्यंग आढळल्यास संदर्भित केले का?',

  // NRC fields
  age: 'वय',
  ageUnit: 'वर्ष/महिने',
  anganwadiCenter: 'अंगणवाडी केंद्र',
  weightKg: 'वजन (किलो)',
  heightCm: 'उंची/लांबी (से.मी.)',
  muacCm: 'MUAC (से.मी.)',
  bilateralPittingEdema: 'सूज आहे का',
  appetiteTest: 'भूक चाचणी',
  referralCategory: 'संदर्भाचे कारण',
  symptomsObservations: 'लक्षणे/निरीक्षणे',
  ashaContactNumber: 'ASHA संपर्क क्रमांक',
  referralDate: 'संदर्भ तारीख',
  anmSignature: 'ANM स्वाक्षरी',

  // SNCU fields
  birthWeightGrams: 'जन्माचे वजन (ग्रॅम)',
  villageOrHealthSubcenter: 'गाव / आरोग्य उपकेंद्र',
  sncuDischargeDate: 'SNCU डिस्चार्ज तारीख',
  weightGrams: 'वजन (ग्रॅम)',
  dangerSignsOther: 'इतर धोक्याचे चिन्ह',
  actionTaken: 'कार्यवाही / सल्ला',
  followUpCompletedDate: 'पाठपुरावा पूर्ण झाल्याची तारीख',
  fever: 'ताप',
  breathing_difficulty: 'श्वास घेण्यास त्रास',
  body_turning_blue: 'अंग निळे पडणे',
  refusing_feed: 'खाण्यास नकार/स्तनपान न करणे',
  lethargy_unconscious: 'सुस्ती/बेशुद्ध',
  increasing_jaundice: 'कावीळ वाढणे',
  bleeding: 'रक्तस्राव',
};

// Internal fields that are never shown (the masked Aadhaar is shown instead)
const HIDDEN_FIELDS = ['motherAadhaarLast4', 'motherAgeMonths', 'additionalBabies'];

const mrDigits = (n) => String(n).replace(/\d/g, (d) => '०१२३४५६७८९'[d]);
const babyTitle = (i, name) => `बाळ क्र. ${mrDigits(i + 1)}${name ? ` (${name})` : ''}`;

const BIRTH_ORDER_LABELS = { 1: 'पहिली', 2: 'दुसरी', 3: 'तिसरी' };

// Display formatting for HBNC fields whose stored value isn't human-friendly
const formatFieldValue = (key, value, section = {}) => {
  if (value === null || value === undefined || value === '') return value;
  switch (key) {
    case 'birthOrder': {
      const n = parseInt(value, 10);
      return n >= 4 ? 'चौथी किंवा अधिक' : BIRTH_ORDER_LABELS[n] || value;
    }
    case 'motherAbha': {
      const d = String(value).replace(/\D/g, '');
      return d.length === 14 ? `${d.slice(0, 2)}-${d.slice(2, 6)}-${d.slice(6, 10)}-${d.slice(10)}` : value;
    }
    case 'motherAge':
      return section.motherAgeMonths ? `${value} वर्षे ${section.motherAgeMonths} महिने` : `${value} वर्षे`;
    case 'pretermDelivery':
      return value === 'yes' ? 'होय' : value === 'no' ? 'नाही' : value;
    default:
      return value;
  }
};

// HBNC visit schedule — mirrors app/src/utils/hbncRules.js: home delivery
// from day 1, facility from day 3; days 14/21/28/42 can only be filled on
// their own day (delivery date = day 1), so an unfilled one is "missed" once
// that day has passed.
const HBNC_VISIT_DAYS = [1, 3, 7, 14, 21, 28, 42];
const APPROVABLE_FORM_TYPES = ['hbnc', 'hbyc', 'nrc', 'sncu'];
const AUTO_VISIT_DAYS = [14, 21, 28, 42];
const parseDMY = (str) => {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(str || '');
  return m ? new Date(+m[3], +m[2] - 1, +m[1]) : null;
};
const formatDMY = (d) =>
  `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;

const hbncVisitNote = (day, visitData, motherNewborn = {}) => {
  if (visitData?.date && visitData?.babyWeightKg) return null; // done
  if (day === 1 && motherNewborn.deliveryPlace && motherNewborn.deliveryPlace !== 'home') {
    return 'लागू नाही — संस्थेत प्रसूती (भेटी दिवस ३ पासून)';
  }
  if (!AUTO_VISIT_DAYS.includes(day)) return null;
  const delivery = parseDMY(motherNewborn.deliveryDate);
  if (!delivery) return null;
  const due = new Date(delivery);
  due.setDate(due.getDate() + day - 1);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (due < today) return `भेट चुकली — ${formatDMY(due)} रोजी भरायची होती`;
  if (due > today) return `नियोजित — ${formatDMY(due)}`;
  return `आज भरायची आहे — ${formatDMY(due)}`;
};

function FormDetail() {
  const { formType, formId } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [approvingVisit, setApprovingVisit] = useState(null);
  const [approvalError, setApprovalError] = useState(null);

  useEffect(() => {
    fetchFormDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formType, formId]);

  const fetchFormDetail = async () => {
    setLoading(true);
    try {
      let response;
      // Fetch specific form by ID using dedicated endpoints
      if (formType === 'hbnc') {
        response = await adminAPI.getHBNCForm(formId);
        setForm({ ...response.data, type: 'HBNC' });
      } else if (formType === 'hbyc') {
        response = await adminAPI.getHBYCForm(formId);
        setForm({ ...response.data, type: 'HBYC' });
      } else if (formType === 'nrc') {
        response = await adminAPI.getNRCForm(formId);
        setForm({ ...response.data, type: 'NRC' });
      } else if (formType === 'sncu') {
        response = await adminAPI.getSNCUForm(formId);
        setForm({ ...response.data, type: 'SNCU' });
      }

      setLoading(false);
    } catch (err) {
      console.error('Failed to fetch form:', err);
      if (err.response?.status === 404) {
        setError('फॉर्म सापडला नाही');
      } else if (err.response?.status === 403) {
        setError('फॉर्म पाहण्याची परवानगी नाही');
      } else {
        setError('फॉर्म लोड करण्यात अयशस्वी');
      }
      setLoading(false);
    }
  };

  const getFieldLabel = (key) => {
    return FIELD_LABELS[key] || key;
  };

  const handleApproveVisit = async (visitKey) => {
    setApprovingVisit(visitKey);
    setApprovalError(null);
    try {
      await approvalsAPI.approveVisit(formType, formId, visitKey, {});
      await fetchFormDetail();
    } catch (err) {
      setApprovalError({ visitKey, message: err.response?.data?.detail || 'मंजुरी देताना त्रुटी आली' });
    } finally {
      setApprovingVisit(null);
    }
  };

  const renderField = (label, value) => (
    <Grid container spacing={2} sx={{ mb: 2 }}>
      <Grid item xs={4}>
        <Typography variant="body2" color="textSecondary" fontWeight="bold">
          {label}
        </Typography>
      </Grid>
      <Grid item xs={8}>
        <Typography variant="body1">{value || '-'}</Typography>
      </Grid>
    </Grid>
  );

  const renderVisitCard = (visitKey, visitData, visitTitle, note = null) => {
    if (!visitData && !note) return null;

    const canApprove = APPROVABLE_FORM_TYPES.includes(formType) && visitKey.startsWith('visit_') && !!visitData?.date;
    const isApproved = visitData?.approvalStatus === 'approved';
    const isApproving = approvingVisit === visitKey;

    return (
      <Paper key={visitKey} sx={{ p: 2, mb: 2, backgroundColor: '#f9f9f9' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1 }}>
          <Typography variant="h6" color="primary">
            {visitTitle}
            {note && <Chip label={note} size="small" color="warning" variant="outlined" sx={{ ml: 1 }} />}
          </Typography>
          {canApprove && (isApproved ? (
            <Chip label="मंजूर केले" size="small" color="success" />
          ) : (
            <Button
              size="small"
              variant="contained"
              color="success"
              disabled={isApproving}
              onClick={() => handleApproveVisit(visitKey)}
              sx={{ textTransform: 'none', fontWeight: 600, boxShadow: 'none' }}
            >
              {isApproving ? 'मंजूर होत आहे...' : 'मंजूर करा'}
            </Button>
          ))}
        </Box>
        {approvalError?.visitKey === visitKey && (
          <Typography variant="body2" color="error" sx={{ mb: 1 }}>{approvalError.message}</Typography>
        )}
        {!visitData ? null : <Divider sx={{ mb: 2 }} />}
        {visitData?.additionalBabies?.length > 0 && (
          <Typography variant="caption" color="textSecondary" component="div" sx={{ mb: 1 }}>
            खालील माहिती: आईचे प्रश्न + {babyTitle(0, form?.formData?.motherNewborn?.babyName)}
          </Typography>
        )}
        {visitData && (
        <Grid container spacing={2}>
          {Object.entries(visitData).map(([key, value]) => {
            if (typeof value === 'object' && value !== null) {
              return null; // Skip nested objects
            }
            return (
              <Grid item xs={12} sm={6} key={key}>
                <Typography variant="body2" color="textSecondary">
                  {getFieldLabel(key)}
                </Typography>
                <Typography variant="body1">
                  {Array.isArray(value) ? value.join(', ') : value?.toString() || '-'}
                </Typography>
              </Grid>
            );
          })}
        </Grid>
        )}
        {(visitData?.additionalBabies || []).map((baby, idx) => baby && (
          <Box key={idx} sx={{ mt: 2 }}>
            <Typography variant="subtitle1" color="secondary" fontWeight="bold">
              {babyTitle(idx + 1, form?.formData?.motherNewborn?.additionalBabies?.[idx]?.babyName)}
            </Typography>
            <Divider sx={{ mb: 1 }} />
            <Grid container spacing={2}>
              {Object.entries(baby).filter(([, v]) => v !== null && !(Array.isArray(v) && v.length === 0)).map(([key, value]) => (
                <Grid item xs={12} sm={6} key={key}>
                  <Typography variant="body2" color="textSecondary">{getFieldLabel(key)}</Typography>
                  <Typography variant="body1">{Array.isArray(value) ? value.join(', ') : value?.toString() || '-'}</Typography>
                </Grid>
              ))}
            </Grid>
          </Box>
        ))}
      </Paper>
    );
  };

  const renderHBNCContent = () => {
    if (!form?.formData) return null;

    const tabs = [
      { label: 'ओळख', key: 'identification' },
      { label: 'आई आणि बाळ', key: 'motherNewborn' },
      { label: 'भेटी', key: 'visits' },
      // आरोग्य तपासणी व समुपदेशन आता प्रत्येक भेटीत (visits.visit_X) नोंदवले
      // जातात, त्यामुळे स्वतंत्र टॅब म्हणून दाखवले जात नाहीत — ती माहिती
      // 'भेटी' टॅबखालील प्रत्येक भेट कार्डावर आपोआप दिसते (renderVisitCard).
      // { label: 'आरोग्य तपासणी', key: 'healthAssessment' },
      // { label: 'समुपदेशन', key: 'counseling' },
      { label: 'परिणाम', key: 'outcome' },
    ];

    const renderTabContent = () => {
      const currentTab = tabs[activeTab];
      const data = form.formData[currentTab.key];

      if (!data) {
        return <Typography color="textSecondary">माहिती उपलब्ध नाही</Typography>;
      }

      // Special handling for visits
      if (currentTab.key === 'visits') {
        const motherNewborn = form.formData.motherNewborn || {};
        const signatureKeys = [
          'ashaSignature', 'ashaSignatureDate', 'gatpravartakSignature',
          'supervisorSignature', 'supervisorSignatureDate',
        ];
        const hasSignatures = signatureKeys.some((k) => data[k]);
        return (
          <Box>
            {HBNC_VISIT_DAYS.map(day => {
              const visitData = data[`visit_${day}`];
              return renderVisitCard(
                `visit_${day}`,
                visitData,
                `भेट ${day} (दिवस ${day})`,
                hbncVisitNote(day, visitData, motherNewborn)
              );
            })}
            {hasSignatures && (
              <Paper sx={{ p: 2, mb: 2, backgroundColor: '#f9f9f9' }}>
                <Typography variant="h6" gutterBottom color="primary">स्वाक्षऱ्या</Typography>
                <Divider sx={{ mb: 2 }} />
                {signatureKeys.map((k) => (
                  <Box key={k}>{renderField(getFieldLabel(k), data[k])}</Box>
                ))}
              </Paper>
            )}
          </Box>
        );
      }

      // Regular field display for other sections
      return (
        <Grid container spacing={2}>
          {Object.entries(data).filter(([key]) => !HIDDEN_FIELDS.includes(key)).map(([key, value]) => {
            if (typeof value === 'object' && value !== null) {
              return (
                <Grid item xs={12} key={key}>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    {getFieldLabel(key)}
                  </Typography>
                  <Box sx={{ pl: 2 }}>
                    {Array.isArray(value) ? (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {value.map((item, idx) => (
                          <Chip key={idx} label={item} size="small" />
                        ))}
                      </Box>
                    ) : (
                      Object.entries(value).map(([subKey, subValue]) => (
                        <Typography key={subKey} variant="body2" sx={{ mb: 1 }}>
                          <strong>{getFieldLabel(subKey)}:</strong> {subValue?.toString() || '-'}
                        </Typography>
                      ))
                    )}
                  </Box>
                </Grid>
              );
            }
            return (
              <Grid item xs={12} sm={6} key={key}>
                {renderField(
                  getFieldLabel(key),
                  Array.isArray(value) ? value.join(', ') : formatFieldValue(key, value, data)
                )}
              </Grid>
            );
          })}
          {currentTab.key === 'motherNewborn' && (data.additionalBabies || []).map((baby, idx) => baby && (
            <Grid item xs={12} key={`baby-${idx}`}>
              <Paper sx={{ p: 2, backgroundColor: '#f9f9f9' }}>
                <Typography variant="subtitle1" color="primary" fontWeight="bold" gutterBottom>
                  नवजात माहिती — {babyTitle(idx + 1, baby.babyName)}
                </Typography>
                <Grid container spacing={2}>
                  {Object.entries(baby).map(([key, value]) => (
                    <Grid item xs={12} sm={6} key={key}>
                      {renderField(getFieldLabel(key), value)}
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            </Grid>
          ))}
        </Grid>
      );
    };

    return (
      <>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
            variant="scrollable"
            scrollButtons="auto"
          >
            {tabs.map((tab, index) => (
              <Tab key={index} label={tab.label} />
            ))}
          </Tabs>
        </Box>
        <Box>{renderTabContent()}</Box>
      </>
    );
  };

  const renderHBYCContent = () => {
    if (!form?.formData) return null;

    const tabs = [
      { label: 'बाळ प्रोफाइल', key: 'childProfile' },
      { label: 'भेटी (३-१५ महिने)', key: 'visits' },
      { label: 'परिणाम', key: 'outcome' },
    ];

    const renderTabContent = () => {
      const currentTab = tabs[activeTab];
      const data = form.formData[currentTab.key];

      if (!data) {
        return <Typography color="textSecondary">माहिती उपलब्ध नाही</Typography>;
      }

      // Special handling for visits
      if (currentTab.key === 'visits') {
        const visitMonths = [3, 6, 9, 12, 15];
        return (
          <Box>
            {visitMonths.map(month => {
              const visitData = data[`visit_${month}`];
              return renderVisitCard(
                `visit_${month}`,
                visitData,
                `भेट ${month} महिने`
              );
            })}
          </Box>
        );
      }

      // Regular field display for other sections
      return (
        <Grid container spacing={2}>
          {Object.entries(data).map(([key, value]) => {
            if (typeof value === 'object' && value !== null) {
              return (
                <Grid item xs={12} key={key}>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    {getFieldLabel(key)}
                  </Typography>
                  <Box sx={{ pl: 2 }}>
                    {Array.isArray(value) ? (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {value.map((item, idx) => (
                          <Chip key={idx} label={item} size="small" />
                        ))}
                      </Box>
                    ) : (
                      Object.entries(value).map(([subKey, subValue]) => (
                        <Typography key={subKey} variant="body2" sx={{ mb: 1 }}>
                          <strong>{getFieldLabel(subKey)}:</strong> {subValue?.toString() || '-'}
                        </Typography>
                      ))
                    )}
                  </Box>
                </Grid>
              );
            }
            return (
              <Grid item xs={12} sm={6} key={key}>
                {renderField(getFieldLabel(key), Array.isArray(value) ? value.join(', ') : value)}
              </Grid>
            );
          })}
        </Grid>
      );
    };

    return (
      <>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
            variant="scrollable"
            scrollButtons="auto"
          >
            {tabs.map((tab, index) => (
              <Tab key={index} label={tab.label} />
            ))}
          </Tabs>
        </Box>
        <Box>{renderTabContent()}</Box>
      </>
    );
  };

  const renderNRCContent = () => {
    if (!form?.formData) return null;

    const tabs = [
      { label: 'बालकाची माहिती', key: 'childDetails' },
      { label: 'पोषणविषयक तपासणी', key: 'nutritionalAssessment' },
      { label: 'संदर्भाचे कारण', key: 'referralReason' },
      { label: 'आशा/आरोग्य कर्मचारी तपशील', key: 'ashaAnmDetails' },
      { label: 'पाठपुरावा भेटीची नोंद', key: 'followUp' },
    ];

    const renderTabContent = () => {
      const currentTab = tabs[activeTab];
      const data = form.formData[currentTab.key];

      if (!data) {
        return <Typography color="textSecondary">माहिती उपलब्ध नाही</Typography>;
      }

      return (
        <Grid container spacing={2}>
          {Object.entries(data).map(([key, value]) => {
            if (typeof value === 'object' && value !== null) {
              return (
                <Grid item xs={12} key={key}>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    {getFieldLabel(key)}
                  </Typography>
                  <Box sx={{ pl: 2 }}>
                    {Array.isArray(value) ? (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {value.map((item, idx) => (
                          <Chip key={idx} label={item} size="small" />
                        ))}
                      </Box>
                    ) : (
                      Object.entries(value).map(([subKey, subValue]) => (
                        <Typography key={subKey} variant="body2" sx={{ mb: 1 }}>
                          <strong>{getFieldLabel(subKey)}:</strong> {subValue?.toString() || '-'}
                        </Typography>
                      ))
                    )}
                  </Box>
                </Grid>
              );
            }
            return (
              <Grid item xs={12} sm={6} key={key}>
                {renderField(getFieldLabel(key), Array.isArray(value) ? value.join(', ') : value)}
              </Grid>
            );
          })}
        </Grid>
      );
    };

    return (
      <>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
            variant="scrollable"
            scrollButtons="auto"
          >
            {tabs.map((tab, index) => (
              <Tab key={index} label={tab.label} />
            ))}
          </Tabs>
        </Box>
        <Box>{renderTabContent()}</Box>
      </>
    );
  };

  const renderSNCUContent = () => {
    if (!form?.formData) return null;

    const tabs = [
      { label: 'बाळाची माहिती', key: 'childInfo' },
      { label: 'पाठपुरावा भेटींची नोंद', key: 'followUp' },
    ];

    const renderTabContent = () => {
      const currentTab = tabs[activeTab];
      const data = form.formData[currentTab.key];

      if (!data) {
        return <Typography color="textSecondary">माहिती उपलब्ध नाही</Typography>;
      }

      return (
        <Grid container spacing={2}>
          {Object.entries(data).map(([key, value]) => {
            if (typeof value === 'object' && value !== null) {
              return (
                <Grid item xs={12} key={key}>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    {getFieldLabel(key)}
                  </Typography>
                  <Box sx={{ pl: 2 }}>
                    {Array.isArray(value) ? (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {value.map((item, idx) => (
                          <Chip key={idx} label={item} size="small" />
                        ))}
                      </Box>
                    ) : (
                      Object.entries(value).map(([subKey, subValue]) => (
                        <Typography key={subKey} variant="body2" sx={{ mb: 1 }}>
                          <strong>{getFieldLabel(subKey)}:</strong>{' '}
                          {Array.isArray(subValue)
                            ? subValue.map((v) => getFieldLabel(v)).join(', ') || '-'
                            : subValue?.toString() || '-'}
                        </Typography>
                      ))
                    )}
                  </Box>
                </Grid>
              );
            }
            return (
              <Grid item xs={12} sm={6} key={key}>
                {renderField(getFieldLabel(key), Array.isArray(value) ? value.join(', ') : value)}
              </Grid>
            );
          })}
        </Grid>
      );
    };

    return (
      <>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
            variant="scrollable"
            scrollButtons="auto"
          >
            {tabs.map((tab, index) => (
              <Tab key={index} label={tab.label} />
            ))}
          </Tabs>
        </Box>
        <Box>{renderTabContent()}</Box>
      </>
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !form) {
    return (
      <Container>
        <Typography color="error">{error || 'फॉर्म सापडला नाही'}</Typography>
        <Button onClick={() => navigate(-1)} sx={{ mt: 2 }}>
          परत जा
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" alignItems="center" mb={3}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{ mr: 2 }}
        >
          परत
        </Button>
        <Typography variant="h4">
          {form.type} फॉर्म तपशील
        </Typography>
      </Box>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          मूलभूत माहिती
        </Typography>
        <Divider sx={{ mb: 2 }} />
        {renderField('आशा कार्यकर्ता', form.ashaWorkerName)}
        {renderField('तारीख', new Date((form.createdAt.endsWith('Z') ? form.createdAt : form.createdAt + 'Z')).toLocaleString('mr-IN', { timeZone: 'Asia/Kolkata' }))}
        {renderField('स्थिती', form.syncStatus === 'synced' ? 'समक्रमित' : 'प्रलंबित')}
      </Paper>

      <Paper sx={{ p: 3 }}>
        {form.type === 'HBNC' && renderHBNCContent()}
        {form.type === 'HBYC' && renderHBYCContent()}
        {form.type === 'NRC' && renderNRCContent()}
        {form.type === 'SNCU' && renderSNCUContent()}
      </Paper>
    </Container>
  );
}

export default FormDetail;
